import { type DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { FavoriteDocumentCard } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/recents/FavoriteDocumentCard'
import { RecentChartCard } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/recents/RecentChartCard'
import { RecentItems } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/recents/RecentItems'
import {
  type ChartSearchItem,
  type RecentItem,
  type SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import type { FavoriteDocument } from 'features/knowledgeDocumentViewer/utils/favoriteDocumentsStorage'
import React, { memo } from 'react'

import { type JumpToLinkCardItem, JumpToLinkCards } from './JumpToLinkCards'
import {
  EdgeFade,
  FavoritesGrid,
  ScrollContent,
  ScrollRow,
  ScrollTrack,
  SectionContainer,
  SectionLabel,
} from './unifiedSearchLandingStyles'

export type LandingScrollSectionsProps = {
  jumpToItems: JumpToLinkCardItem[]
  favoriteDocuments: FavoriteDocument[]
  displayRecentDocuments: RecentItem[]
  recentCharts: RecentItem<ChartSearchItem>[]
  onFavoriteSelect: (documentId: string) => void
  onRemoveFavorite: (documentId: string) => void
  onRecentDocumentSelect: (doc: DocumentTableRow) => void
  onSensorSelect: (sensor: SensorSearchItem) => void
  onChartSelect: (chart: ChartSearchItem) => void
  isFavorited: (id: string) => boolean
  onToggleFavorite: (document: DocumentTableRow) => void
}

export const LandingScrollSections = memo(function LandingScrollSections({
  jumpToItems,
  favoriteDocuments,
  displayRecentDocuments,
  recentCharts,
  onFavoriteSelect,
  onRemoveFavorite,
  onRecentDocumentSelect,
  onSensorSelect,
  onChartSelect,
  isFavorited,
  onToggleFavorite,
}: LandingScrollSectionsProps) {
  return (
    <>
      <ScrollRow>
        <ScrollTrack>
          <ScrollContent>
            <SectionContainer>
              <SectionLabel>JUMP TO</SectionLabel>
              <JumpToLinkCards items={jumpToItems} />
            </SectionContainer>

            {favoriteDocuments.length > 0 && (
              <SectionContainer>
                <SectionLabel>FAVORITES</SectionLabel>
                <FavoritesGrid>
                  {favoriteDocuments.map((doc) => (
                    <FavoriteDocumentCard
                      key={doc.id}
                      document={doc}
                      onClick={onFavoriteSelect}
                      onRemoveFavorite={onRemoveFavorite}
                    />
                  ))}
                </FavoritesGrid>
              </SectionContainer>
            )}

            {recentCharts.length > 0 && (
              <SectionContainer>
                <SectionLabel>Recent Charts</SectionLabel>
                <FavoritesGrid>
                  {recentCharts.map((recent) => (
                    <RecentChartCard
                      key={recent.item.id}
                      chart={recent.item}
                      onClick={onChartSelect}
                    />
                  ))}
                </FavoritesGrid>
              </SectionContainer>
            )}

            {displayRecentDocuments.length > 0 && (
              <SectionContainer>
                <SectionLabel>Recent Searches</SectionLabel>
                <RecentItems
                  recentItems={displayRecentDocuments}
                  onSelectDocument={onRecentDocumentSelect}
                  onSelectSensor={onSensorSelect}
                  onSelectChart={onChartSelect}
                  isFavorited={isFavorited}
                  onToggleFavorite={onToggleFavorite}
                  fullPage
                />
              </SectionContainer>
            )}
          </ScrollContent>
        </ScrollTrack>
      </ScrollRow>
      <EdgeFade $placement='bottom' aria-hidden />
    </>
  )
})
